import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Popover,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getVoltageRatio,
  createVoltageRatio,
  updateVoltageRatio,
  deleteVoltageRatio,
} from "../../../Services/Services";
import InfiniteScroll from "react-infinite-scroll-component";

const VoltageRatioManagementTable = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [filter, setFilter] = useState("");
  const [nameError, setNameError] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [voltageRatioToDelete, setVoltageRatioToDelete] = useState(null);
  const [voltageRatios, setVoltageRatios] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [voltageRatioForm, setVoltageRatioForm] = useState({
    name: "",
    created_by: localStorage.getItem("user_id"),
  });

  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [multiVoltageRatios, setMultiVoltageRatios] = useState([{ name: "" }]);

  // ── Theme-aware helpers ──
  const tableHeadBg = isDark
    ? alpha(theme.palette.primary.main, 0.15)
    : "#f0f4ff";

  const rowBg = (index) =>
    index % 2 === 0
      ? isDark
        ? alpha(theme.palette.common.white, 0.035)
        : "#fafafa"
      : theme.palette.background.paper;

  // ── Fetch ──
  const fetchVoltageRatios = async (
    reset = false,
    pageNo = 1,
    search = filter
  ) => {
    try {
      const data = await getVoltageRatio(pageNo, search);
      if (reset) {
        setVoltageRatios(data.results);
      } else {
        setVoltageRatios((prev) => [...prev, ...data.results]);
      }
      setHasMore(!!data.next);
      setPage(pageNo + 1);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch Voltage Ratios");
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchVoltageRatios(true, 1, filter);
    }, 500);
    return () => clearTimeout(handler);
  }, [filter]);

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  // ── Save (Add / Edit) ──
  const handleSave = async (nameParam) => {
    const createdBy = localStorage.getItem("user_id");
    const finalName = nameParam || voltageRatioForm.name;

    if (!finalName.trim()) {
      setNameError("Voltage Ratio name is required");
      return;
    }
    setNameError("");

    try {
      if (editId) {
        const update_res = await updateVoltageRatio(editId, {
          name: finalName,
        });
        setVoltageRatios(
          voltageRatios.map((vr) =>
            vr.id === editId ? { ...vr, ...update_res } : vr
          )
        );
        toast.success("Voltage Ratio updated successfully");
      } else {
        const response = await createVoltageRatio({
          name: finalName,
          created_by: createdBy,
        });
        toast.success("Voltage Ratio added successfully");
        if (Array.isArray(response)) {
          setVoltageRatios([...response, ...voltageRatios]);
        } else {
          setVoltageRatios([response, ...voltageRatios]);
        }
      }
      setVoltageRatioForm({ name: "", created_by: createdBy });
      setEditId(null);
      setOpen(false);
    } catch (err) {
      const msg =
        err?.response?.data?.name?.[0] ||
        err?.response?.data?.detail ||
        "Failed to save";
      toast.error(Array.isArray(msg) ? msg[0] : msg);
      if (msg.toString().toLowerCase().includes("already exists")) {
        setNameError("VoltageRatio with this name already exists.");
      }
    }
  };

  const handleSubmitAll = async () => {
    const createdBy = localStorage.getItem("user_id");
    const validRatios = multiVoltageRatios
      .filter((c) => c.name.trim() !== "")
      .map((c) => ({
        name: c.name.trim(),
        created_by: createdBy,
      }));

    if (validRatios.length === 0) {
      setNameError("Please enter at least one Voltage Ratio");
      return;
    }
    try {
      const response = await createVoltageRatio(validRatios);
      toast.success("Voltage Ratios added successfully");
      if (Array.isArray(response)) {
        setVoltageRatios([...response, ...voltageRatios]);
      } else {
        setVoltageRatios([response, ...voltageRatios]);
      }
      setMultiVoltageRatios([{ name: "" }]);
      setOpen(false);
    } catch (err) {
      const msg =
        err?.response?.data?.[0]?.name?.[0] ||
        err?.response?.data?.name?.[0] ||
        "Failed to save";
      toast.error(Array.isArray(msg) ? msg[0] : msg);
    }
  };

  const handleEdit = (vr) => {
    setVoltageRatioForm({ name: vr.name });
    setEditId(vr.id);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteVoltageRatio(id);
      setVoltageRatios((prev) => prev.filter((vr) => vr.id !== id));
      toast.success("Deleted successfully");
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const handleAddField = () => {
    if (multiVoltageRatios.length >= 5) {
      toast.error("You can add maximum 5 Voltage Ratios");
      return;
    }
    setMultiVoltageRatios([...multiVoltageRatios, { name: "" }]);
  };

  const handleRemoveField = (index) => {
    setMultiVoltageRatios(multiVoltageRatios.filter((_, i) => i !== index));
  };

  const handleChange = (index, value) => {
    const newFields = [...multiVoltageRatios];
    newFields[index].name = value;
    setMultiVoltageRatios(newFields);
  };

  function formatOnlyDate(dateTimeString) {
    if (!dateTimeString) return "N/A";
    const datePart = dateTimeString.split("T")[0];
    const [year, month, day] = datePart.split("-");
    return `${day}-${month}-${year}`;
  }

  const closeDialog = () => {
    setOpen(false);
    setEditId(null);
    setVoltageRatioForm({ name: "" });
    setMultiVoltageRatios([{ name: "" }]);
    setNameError("");
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100%" }}>
      {/* ── Search + Add Bar ── */}
      <Box
        display="flex"
        flexWrap="wrap"
        gap={2}
        alignItems="center"
        sx={{
          mt: { xs: 3, md: 1 },
          mb: 1,
          position: "sticky",
          top: 0,
          zIndex: 1100,
          pt: 1,
          pb: 1,
          px: 0.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
          backdropFilter: "blur(8px)",
          boxShadow: isDark
            ? "0 2px 10px rgba(0,0,0,0.45)"
            : "0 1px 4px rgba(0,0,0,0.07)",
        }}
      >
        {/* Search Field */}
        <Box sx={{ flexGrow: 1, minWidth: 200 }}>
          <TextField
            label="Search by Voltage Ratio"
            variant="outlined"
            size="small"
            fullWidth
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="e.g. 11kV/433V"
            sx={{
              "& .MuiInputBase-root": {
                height: 30,
                fontSize: "0.7rem",
                borderRadius: 0,
              },
              "& .MuiInputLabel-root": { fontSize: "0.7rem" },
            }}
          />
        </Box>

        {/* Add Button */}
        <Box sx={{ ml: "15px" }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            sx={{
              minHeight: "30px",
              fontSize: "0.7rem",
              padding: "4px 10px",
              borderRadius: "0px",
            }}
            onClick={() => {
              setEditId(null);
              setVoltageRatioForm({ name: "" });
              setMultiVoltageRatios([{ name: "" }]);
              setNameError("");
              setOpen(true);
            }}
          >
            Voltage Ratio
          </Button>
        </Box>
      </Box>

      {/* ── Table / Cards ── */}
      <div id="project-table-print">
        <InfiniteScroll
          dataLength={voltageRatios.length}
          next={() => fetchVoltageRatios(false, page, filter)}
          hasMore={hasMore}
          loader={
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography
                sx={{
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  color: "text.secondary",
                }}
              >
                Loading...
              </Typography>
            </Box>
          }
          endMessage={
            <Typography
              sx={{
                textAlign: "center",
                py: 2,
                fontSize: { xs: "0.85rem", sm: "1rem" },
                color: "text.disabled",
              }}
            >
              No more voltage ratios
            </Typography>
          }
        >
          {isMobile ? (
            /* ── MOBILE CARDS ── */
            <Box sx={{ bgcolor: "background.paper" }}>
              {voltageRatios.map((vr, index) => (
                <Box
                  key={vr.id || index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    p: 1.5,
                    bgcolor: rowBg(index),
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    transition: "background-color 0.15s ease",
                    "&:active": { bgcolor: "action.selected" },
                  }}
                >
                  {/* Left Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: "text.primary",
                        mb: 0.3,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <span
                        style={{
                          color: theme.palette.text.disabled,
                          fontSize: "0.75rem",
                        }}
                      >
                        {index + 1}
                      </span>{" "}
                      {vr.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "text.disabled",
                        ml: 1,
                      }}
                    >
                      {formatOnlyDate(vr.created_at)}
                    </Typography>
                  </Box>

                  {/* Action Buttons */}
                  <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                    {/* Edit */}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(vr);
                        setOpen(true);
                      }}
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.primary.main, 0.15)
                            : "#e3f2fd",
                        borderRadius: "4px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha(t.palette.primary.main, 0.28)
                              : "#bbdefb",
                        },
                      }}
                    >
                      <EditIcon
                        sx={{ fontSize: "0.95rem", color: "primary.main" }}
                      />
                    </IconButton>

                    {/* Delete */}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnchorEl(e.currentTarget);
                        setVoltageRatioToDelete(vr.id);
                      }}
                      sx={{
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? alpha(t.palette.error.main, 0.15)
                            : "#ffebee",
                        borderRadius: "4px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? alpha(t.palette.error.main, 0.28)
                              : "#ffcdd2",
                        },
                      }}
                    >
                      <DeleteIcon
                        sx={{ fontSize: "0.95rem", color: "error.main" }}
                      />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            /* ── DESKTOP TABLE ── */
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                maxHeight: "88vh",
                overflowX: "auto",
                overflowY: "auto",
                width: "100%",
                bgcolor: "background.paper",
                backgroundImage: "none",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 0,
                boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : "none",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{
                  minWidth: 1000,
                  "& td, & th": {
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  },
                }}
              >
                <TableHead
                  sx={{
                    "& th": {
                      fontWeight: "bold",
                      color: "text.primary",
                      borderBottom: "2px solid",
                      borderColor: "primary.main",
                      backgroundColor: tableHeadBg,
                      fontSize: "0.75rem",
                      letterSpacing: "0.04em",
                    },
                  }}
                >
                  <TableRow>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 3,
                        backgroundColor: tableHeadBg,
                        minWidth: 50,
                      }}
                    >
                      S.No.
                    </TableCell>
                    <TableCell sx={{ backgroundColor: tableHeadBg }}>
                      Voltage Ratio
                    </TableCell>
                    <TableCell sx={{ backgroundColor: tableHeadBg }}>
                      Date
                    </TableCell>
                    <TableCell
                      className="no-print"
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: tableHeadBg,
                        zIndex: 3,
                        minWidth: 80,
                        padding: 0,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {voltageRatios.map((vr, index) => (
                    <TableRow
                      hover
                      key={vr.id || index}
                      sx={{
                        backgroundColor: rowBg(index),
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: (t) =>
                            t.palette.mode === "dark"
                              ? `${alpha(
                                t.palette.primary.main,
                                0.1
                              )} !important`
                              : "#e3f2fd !important",
                          "& td": {
                            backgroundColor: "inherit !important",
                          },
                        },
                        "& td": {
                          paddingRight: "13px !important",
                          paddingLeft: "13px !important",
                          padding: "0px",
                          fontSize: { xs: "0.65rem", sm: "0.75rem" },
                          whiteSpace: "nowrap",
                          color: "text.primary",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          position: "sticky",
                          left: 0,
                          bgcolor: "inherit",
                          zIndex: 1,
                        }}
                      >
                        {index + 1}
                      </TableCell>
                      <TableCell>{vr.name}</TableCell>
                      <TableCell>{formatOnlyDate(vr.created_at)}</TableCell>
                      <TableCell
                        className="no-print"
                        sx={{
                          position: "sticky",
                          right: 0,
                          bgcolor: "inherit",
                          zIndex: 1,
                          minWidth: 80,
                        }}
                      >
                        <IconButton
                          color="primary"
                          sx={{
                            padding: 0,
                            paddingLeft: 1,
                            paddingRight: 1,
                          }}
                          size="small"
                          onClick={() => {
                            handleEdit(vr);
                            setOpen(true);
                          }}
                        >
                          <EditIcon transform="scale(0.6)" />
                        </IconButton>
                        <IconButton
                          sx={{
                            padding: 0,
                            paddingLeft: 1,
                            paddingRight: 1,
                          }}
                          color="error"
                          size="small"
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setVoltageRatioToDelete(vr.id);
                          }}
                        >
                          <DeleteIcon transform="scale(0.6)" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </InfiniteScroll>
      </div>

      {/* ══════════════════════════════════════════════ */}
      {/* ADD / EDIT DIALOG                             */}
      {/* ══════════════════════════════════════════════ */}
      <Dialog
        open={open}
        onClose={closeDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 12px 40px rgba(0,0,0,0.65)"
              : "0 8px 32px rgba(0,0,0,0.14)",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: "1px solid",
            borderColor: "divider",
            py: 1.5,
            bgcolor: (t) =>
              t.palette.mode === "dark"
                ? alpha(t.palette.primary.main, 0.15)
                : alpha(t.palette.primary.main, 0.06),
            color: "text.primary",
            fontWeight: 700,
            fontSize: "0.95rem",
          }}
        >
          {editId ? "Edit Voltage Ratio" : "Add Voltage Ratio"}
        </DialogTitle>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (editId) {
              handleSave();
            } else {
              handleSubmitAll();
            }
          }}
        >
          <DialogContent
            dividers
            sx={{ bgcolor: "background.paper" }}
          >
            {editId ? (
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <TextField
                  label="Voltage Ratio"
                  placeholder="e.g. 11000/433 V"
                  fullWidth
                  value={voltageRatioForm.name}
                  onChange={(e) => {
                    const value = e.target.value;
                    setVoltageRatioForm((prev) => ({
                      ...prev,
                      name: value,
                    }));
                    if (value.trim()) setNameError("");
                  }}
                  error={!!nameError}
                  helperText={nameError}
                  required
                />
              </Box>
            ) : (
              <>
                {multiVoltageRatios.map((vr, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      gap: 2,
                      mb: 2,
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      label={`Voltage Ratio ${index + 1}`}
                      placeholder="e.g. 11000/433 V"
                      fullWidth
                      value={vr.name}
                      onChange={(e) => handleChange(index, e.target.value)}
                      required={index === 0}
                    />
                    <Box sx={{ width: 40, textAlign: "center" }}>
                      {multiVoltageRatios.length > 1 && (
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleRemoveField(index)}
                          sx={{
                            "&:hover": {
                              bgcolor: (t) =>
                                t.palette.mode === "dark"
                                  ? alpha(t.palette.error.main, 0.15)
                                  : "#ffebee",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                ))}
                {nameError && (
                  <Typography color="error" variant="caption">
                    {nameError}
                  </Typography>
                )}
              </>
            )}
          </DialogContent>

          {/* ADD ROW button row */}
          <DialogActions
            sx={{
              justifyContent: "flex-start",
              mb: -5,
              bgcolor: "background.paper",
            }}
          >
            {!editId && (
              <Button
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                onClick={handleAddField}
                sx={{
                  height: 35,
                  fontSize: 14,
                  padding: "2px 10px",
                  minWidth: "auto",
                  textTransform: "none",
                  border: "none",
                  display: "flex",
                  bgcolor: "transparent",
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: (t) =>
                      t.palette.mode === "dark"
                        ? alpha(t.palette.primary.main, 0.1)
                        : alpha(t.palette.primary.main, 0.06),
                  },
                }}
              >
                ADD ROW
              </Button>
            )}
          </DialogActions>

          {/* Footer Actions */}
          <DialogActions
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              px: 2,
              py: 1.2,
              bgcolor: "background.paper",
            }}
          >
            {/* Close */}
            <Button
              color="error"
              variant="outlined"
              size="small"
              onClick={closeDialog}
            >
              CLOSE
            </Button>

            {/* Save (Add mode) */}
            {!editId && (
              <Button
                type="submit"
                variant="contained"
                size="small"
                sx={{
                  borderRadius: 1,
                  textTransform: "none",
                  fontSize: "0.85rem",
                  padding: "4px 12px",
                }}
              >
                SAVE
              </Button>
            )}

            {/* Update (Edit mode) */}
            {editId && (
              <Button
                type="submit"
                variant="contained"
                size="small"
              >
                Update
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* ── Delete Popover ── */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setVoltageRatioToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            bgcolor: "background.paper",
            backgroundImage: "none",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: isDark
              ? "0 8px 28px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.14)",
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 180 }}>
          <Typography
            variant="body2"
            sx={{ color: "text.primary", mb: 1 }}
          >
            Are you sure you want to delete?
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              mt: 1,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAnchorEl(null);
                setVoltageRatioToDelete(null);
              }}
            >
              No
            </Button>
            <Button
              color="error"
              variant="contained"
              size="small"
              onClick={async () => {
                try {
                  await handleDelete(voltageRatioToDelete);
                } finally {
                  setAnchorEl(null);
                  setVoltageRatioToDelete(null);
                }
              }}
            >
              Yes
            </Button>
          </Box>
        </Box>
      </Popover>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        theme={isDark ? "dark" : "light"}
      />
    </Box>
  );
};

export default VoltageRatioManagementTable;